import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  fetchTodayMaterialReturns,
  receiveMaterialReturn,
  scanMaterialReturnPickup,
  type MaterialReturnTodayItem,
} from '@/features/delman/api/materialReturnApi'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QrCameraScanner } from '@/shared/components/QrCameraScanner'
import { SkeletonList } from '@/shared/components/Skeleton'
import { Toast } from '@/shared/components/Toast'

type View = 'list' | 'scan' | 'receive'

const WRH_PREFIX = 'SUPEROTI:WRH:'

function isWarehouseReturnPayload(value: string): boolean {
  return value.trim().toUpperCase().startsWith(WRH_PREFIX)
}

function statusLabel(status: string): string {
  return delmanTerms.materialReturn.status[status] ?? status
}

function statusBadge(status: string): string {
  if (status === 'picked_up') return 'badge-warning'
  if (status === 'validated') return 'badge-success'
  if (status === 'ready_for_pickup') return 'badge-draft'
  return 'badge-draft'
}

function summaryText(item: MaterialReturnTodayItem): string {
  const total = item.lines.reduce((sum, line) => sum + line.qty_kitchen, 0)
  return delmanTerms.materialReturn.summary.join([
    delmanTerms.materialReturn.summary.materialCount(item.lines.length),
    `${total} qty`,
  ])
}

/** Scan pickup + terima retur dapur→gudang (slice 2b §6). */
export function DelmanMaterialReturnPage() {
  const device = getDelmanDevice()

  const [view, setView] = useState<View>('list')
  const [returns, setReturns] = useState<MaterialReturnTodayItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [toast, setToast] = useState<string | null>(null)

  const [scanInput, setScanInput] = useState('')
  const [cameraActive, setCameraActive] = useState(true)
  const [isScanning, setIsScanning] = useState(false)
  const [manualScanOpen, setManualScanOpen] = useState(false)

  const [selectedReturn, setSelectedReturn] = useState<MaterialReturnTodayItem | null>(null)
  const [pinOpen, setPinOpen] = useState(false)
  const [isReceiving, setIsReceiving] = useState(false)

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(false)
    fetchTodayMaterialReturns()
      .then((res) => {
        if (!cancelled) setReturns(res)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => reload(), [reload, refreshKey])

  useEffect(() => {
    function onRefresh() {
      if (document.visibilityState === 'hidden') return
      setRefreshKey((k) => k + 1)
    }
    window.addEventListener('focus', onRefresh)
    document.addEventListener('visibilitychange', onRefresh)
    return () => {
      window.removeEventListener('focus', onRefresh)
      document.removeEventListener('visibilitychange', onRefresh)
    }
  }, [])

  useEffect(() => {
    const hasPending = returns.some((item) => item.status === 'picked_up')
    if (!hasPending) return undefined
    const timer = window.setInterval(() => setRefreshKey((k) => k + 1), 8_000)
    return () => window.clearInterval(timer)
  }, [returns])

  useEffect(() => {
    if (view === 'scan') {
      setCameraActive(true)
      setIsScanning(false)
    }
  }, [view])

  const pickedUp = useMemo(() => returns.filter((item) => item.status === 'picked_up'), [returns])
  const validated = useMemo(() => returns.filter((item) => item.status === 'validated'), [returns])

  const processPickupScan = useCallback(
    async (raw: string) => {
      const payload = raw.trim()
      if (!payload) {
        setToast(delmanTerms.materialReturn.toast.scanRequired)
        return
      }
      if (!isWarehouseReturnPayload(payload)) {
        setToast(delmanTerms.materialReturn.toast.invalidQr)
        return
      }

      setIsScanning(true)
      setCameraActive(false)
      try {
        const res = await scanMaterialReturnPickup(payload)
        setToast(delmanTerms.materialReturn.toast.pickupSuccess(res.return_code))
        setScanInput('')
        setView('list')
        setRefreshKey((k) => k + 1)
      } catch (e) {
        setToast(e instanceof Error ? e.message : delmanTerms.materialReturn.toast.pickupFailed)
        setCameraActive(true)
        setIsScanning(false)
      } finally {
        setIsScanning(false)
      }
    },
    [],
  )

  const handleCameraScan = useCallback(
    (value: string) => {
      void processPickupScan(value)
    },
    [processPickupScan],
  )

  function openReceive(item: MaterialReturnTodayItem) {
    setSelectedReturn(item)
    setView('receive')
  }

  async function handleReceive(pin: string) {
    if (!selectedReturn) {
      throw new Error(delmanTerms.materialReturn.toast.incompleteData)
    }

    setIsReceiving(true)
    try {
      await receiveMaterialReturn(
        selectedReturn.id,
        pin,
        selectedReturn.lines.map((line) => ({
          material_id: line.material_id,
          qty_received: line.qty_kitchen,
          uom: line.uom,
        })),
      )
      setPinOpen(false)
      setSelectedReturn(null)
      setView('list')
      setToast(delmanTerms.materialReturn.toast.receiveSuccess(selectedReturn.code))
      setRefreshKey((k) => k + 1)
    } finally {
      setIsReceiving(false)
    }
  }

  return (
    <section className="space-y-4">
      <div className="card relative space-y-2">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={isLoading}
          aria-label={delmanTerms.materialReturn.refreshAria}
          title={delmanTerms.materialReturn.refreshAria}
        >
          <span className={`material-symbols-outlined ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
        <div className="pr-12">
          {view !== 'list' && (
            <button
              type="button"
              className="text-body-sm text-primary inline-flex items-center gap-1 mb-2"
              onClick={() => {
                setView('list')
                setSelectedReturn(null)
                setPinOpen(false)
              }}
            >
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>
              {delmanTerms.materialReturn.backToList}
            </button>
          )}
          <p className="page-title break-words">{delmanTerms.materialReturn.pageTitle}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">
            {device ? delmanTerms.materialReturn.subtitle(device.delmanName) : '—'}
          </p>
        </div>
        {view === 'list' && (
          <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2 border border-outline-variant">
            {delmanTerms.materialReturn.intro}
          </p>
        )}
      </div>

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{delmanTerms.materialReturn.loadError}</span>
          <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => setRefreshKey((k) => k + 1)}>
            {delmanTerms.materialReturn.retry}
          </button>
        </div>
      )}

      {isLoading && view === 'list' && <SkeletonList count={3} label={delmanTerms.materialReturn.loading} />}

      {!isLoading && !loadError && view === 'list' && (
        <div className="space-y-4">
          <button type="button" className="btn-primary w-full min-h-[48px]" onClick={() => setView('scan')}>
            {delmanTerms.materialReturn.scanPickupCta}
          </button>

          {pickedUp.length > 0 && (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-status-warning">
                {delmanTerms.materialReturn.pickedUpTitle(pickedUp.length)}
              </p>
              {pickedUp.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="card w-full text-left hover:border-primary transition-colors"
                  onClick={() => openReceive(item)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface break-words">
                        {delmanTerms.materialReturn.fromKitchen(item.kitchen_name)}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">{summaryText(item)}</p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={statusBadge(item.status)}>{statusLabel(item.status)}</span>
                  </div>
                  <p className="text-body-sm text-primary font-semibold mt-2">{delmanTerms.materialReturn.receiveCta}</p>
                </button>
              ))}
            </div>
          )}

          {validated.length > 0 && (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-status-success">{delmanTerms.materialReturn.validatedTitle}</p>
              {validated.map((item) => (
                <div key={item.id} className="card opacity-80">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface break-words">
                        {delmanTerms.materialReturn.fromKitchen(item.kitchen_name)}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">{summaryText(item)}</p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={statusBadge(item.status)}>{statusLabel(item.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pickedUp.length === 0 && validated.length === 0 && (
            <EmptyState
              icon="undo"
              title={delmanTerms.materialReturn.emptyTitle}
              description={delmanTerms.materialReturn.emptyDescription}
            />
          )}
        </div>
      )}

      {view === 'scan' && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-body-sm font-semibold">{delmanTerms.materialReturn.scanTitle}</p>
              {cameraActive && !isScanning && (
                <span className="badge-warning text-body-sm">{delmanTerms.materialReturn.liveBadge}</span>
              )}
            </div>
            <p className="text-body-sm text-on-surface-variant">{delmanTerms.materialReturn.scanHint}</p>
            <QrCameraScanner
              active={cameraActive && !isScanning}
              inactiveMessage={
                isScanning ? delmanTerms.materialReturn.scanProcessing : delmanTerms.materialReturn.cameraHint
              }
              onScan={handleCameraScan}
              unsupportedMessage={delmanTerms.materialReturn.cameraUnsupported}
              permissionDeniedMessage={delmanTerms.materialReturn.cameraPermissionDenied}
              scanningHint={delmanTerms.materialReturn.cameraHint}
            />

            <details
              className="rounded-lg border border-outline-variant/60 bg-surface-container-low"
              open={manualScanOpen}
              onToggle={(e) => setManualScanOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer select-none px-3 py-3 text-body-sm font-medium min-h-[44px] flex items-center gap-2">
                <span className="material-symbols-outlined text-lg text-on-surface-variant">keyboard</span>
                {delmanTerms.materialReturn.manualToggle}
              </summary>
              <div className="space-y-3 border-t border-outline-variant/40 px-3 pb-3 pt-2">
                <label className="field-label" htmlFor="wrh_qr_payload">
                  {delmanTerms.materialReturn.pasteLabel}
                </label>
                <input
                  id="wrh_qr_payload"
                  className="input-field font-mono"
                  placeholder={delmanTerms.materialReturn.pastePlaceholder}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={isScanning}
                />
                <button
                  type="button"
                  className="btn-primary w-full min-h-[48px] disabled:opacity-40"
                  disabled={isScanning || !scanInput.trim()}
                  onClick={() => void processPickupScan(scanInput)}
                >
                  {isScanning ? delmanTerms.materialReturn.scanProcessing : delmanTerms.materialReturn.scanConfirm}
                </button>
              </div>
            </details>
          </div>
        </div>
      )}

      {view === 'receive' && selectedReturn && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-body-lg font-semibold text-on-surface break-words">
              {delmanTerms.materialReturn.fromKitchen(selectedReturn.kitchen_name)}
            </p>
            <p className="text-label-md font-mono text-on-surface-variant">{selectedReturn.code}</p>
            <p className="text-body-sm text-on-surface-variant">{delmanTerms.materialReturn.receiveHint}</p>
          </div>

          <div className="card divide-y divide-surface-variant">
            {selectedReturn.lines.map((line) => (
              <div key={line.id} className="flex items-center justify-between gap-3 p-3">
                <p className="text-body-sm font-semibold text-on-surface break-words">{line.material_name}</p>
                <p className="text-body-lg font-bold text-primary shrink-0">
                  {line.qty_kitchen} {line.uom}
                </p>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn-primary w-full min-h-[48px]"
            disabled={isReceiving}
            onClick={() => setPinOpen(true)}
          >
            {delmanTerms.materialReturn.receiveConfirm}
          </button>
        </div>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={delmanTerms.materialReturn.pinTitle}
        description={
          selectedReturn
            ? delmanTerms.materialReturn.pinDescription(selectedReturn.code, selectedReturn.kitchen_name)
            : delmanTerms.materialReturn.pinFallback
        }
        confirmLabel={delmanTerms.materialReturn.receiveConfirm}
        onClose={() => setPinOpen(false)}
        onConfirm={handleReceive}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </section>
  )
}
